import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDataIfNeeded,
  forceFetchData,
  getCompositeSchemaKey,
  isCompositeSchemaKeyPresent,
} from './DataFetcherSuperUtils';

const useDataFetcher = (fetchingHash, props) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchDataIfNeeded(fetchingHash, props));
  });

  return useSelector((state) => fetchingHash.selector(state, props));
};

const usePaginatedDataFetcher = (fetchingHash, props, reRenderOptions = []) => {
  const [doneFetchingPaginated, setDoneFetchingPaginated] = useState(false);
  const dispatch = useDispatch();
  const schemaKey = getCompositeSchemaKey(props?.paginationData);
  const isPaginatedQuery = Boolean(schemaKey);
  const isSchemaKeyPresent = useSelector((state) => {
    const isSchemaKeyPresentInNamespace = isCompositeSchemaKeyPresent(fetchingHash.schema?.key);
    return isSchemaKeyPresentInNamespace(state, {
      paginationData: props?.paginationData,
    });
  });

  useLayoutEffect(() => {
    let mounted = true;
    if (isPaginatedQuery) {
      setDoneFetchingPaginated(isSchemaKeyPresent);
      dispatch(fetchDataIfNeeded({ schemaKey, ...fetchingHash }, props)).finally(() => {
        if (mounted) {
          setDoneFetchingPaginated(true);
        }
      });
    }

    return () => {
      mounted = false;
    };
  }, [schemaKey, isSchemaKeyPresent, isPaginatedQuery, ...reRenderOptions]);

  return { isPaginatedQuery, doneFetchingPaginated };
};

export const useDataFetcherV2 = (fetchingHash, props, reRenderOptions = []) => {
  const dispatch = useDispatch();
  const [doneFetching, setDoneFetching] = useState(false);
  const { isPaginatedQuery, doneFetchingPaginated } = usePaginatedDataFetcher(fetchingHash, props, reRenderOptions);

  useEffect(() => {
    let mounted = true;

    if (!isPaginatedQuery) {
      if (fetchingHash.alwaysFetch) {
        setDoneFetching(false);
        dispatch(forceFetchData(fetchingHash, props)).finally(() => {
          if (mounted) {
            setDoneFetching(true);
          }
        });
      } else {
        setDoneFetching(false);
        dispatch(fetchDataIfNeeded(fetchingHash, props)).finally(() => {
          if (mounted) {
            setDoneFetching(true);
          }
        });
      }
    }

    return () => {
      mounted = false;
    };
  }, [isPaginatedQuery, ...reRenderOptions]);

  return [doneFetching || doneFetchingPaginated, useSelector((state) => fetchingHash.selector(state, props))];
};

export default useDataFetcher;
